// SideDrawer — a panel that slides in from the left behind a trigger button.
// Shared by the Timeline toolbar affordances (Glossary, Uncurated releases)
// so they read as one system: a button in the toolbar, a titled left panel
// one click away, never eating vertical space in the feed.
import { useState, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

export function SideDrawer({
  trigger,
  title,
  description,
  children,
  width = "max-w-sm",
  open: openProp,
  onOpenChange,
}: {
  /** The always-visible affordance. Must be a single focusable element
   *  (rendered via Radix `asChild`) — e.g. a <button>. Omit when driving the
   *  drawer in controlled mode via `open`/`onOpenChange` (e.g. a whole card
   *  that opens the panel on click). */
  trigger?: ReactNode;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Tailwind max-width class for the panel. Defaults to a slim `max-w-sm`;
   *  pass a wider value (e.g. `max-w-2xl`) for tabular content. */
  width?: string;
  /** Controlled open state. When provided (with `onOpenChange`), the drawer's
   *  visibility is owned by the parent and `trigger` is optional. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40 duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={`fixed inset-y-0 left-0 z-50 flex w-full ${width} flex-col overflow-y-auto border-r border-border bg-card p-6 shadow-xl duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left`}
        >
          <div className="flex items-baseline justify-between gap-3">
            <DialogPrimitive.Title className="font-serif text-xl text-text">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="rounded border border-border-hover px-2.5 py-1 text-xs text-text-secondary hover:border-text/60 hover:text-text"
              aria-label={`Close ${title.toLowerCase()}`}
            >
              Close
            </DialogPrimitive.Close>
          </div>
          {description && (
            <DialogPrimitive.Description className="mt-1 text-sm text-text-secondary">
              {description}
            </DialogPrimitive.Description>
          )}
          <div className="mt-5">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
